import type { SelectChangeEvent } from '@mui/material';
import type { Llm, LlmVersion } from '@type/llm';
import type {
  AriaMode,
  BrailleDisplayKind,
  BrailleDisplayPreset,
  GeneralSettings,
  HoverMode,
  LlmModelSettings,
  LlmSettings,
} from '@type/settings';
import { Check as CheckIcon, Error as ErrorIcon } from '@mui/icons-material';
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Slider,
  Switch,
  TextareaAutosize,
  TextField,
  Typography,
} from '@mui/material';
import { LlmValidationService } from '@service/llmValidation';
import { getValidVersion, MODEL_VERSIONS } from '@service/modelVersions';
import { useViewModel } from '@state/hook/useViewModel';
import {
  MAX_BRAILLE_LINES,
  MAX_BRAILLE_SIZE,
} from '@type/settings';
import {
  clampBrailleLines,
  clampBrailleSize,
  formatMultiLinePreset,
  formatSingleLinePreset,
  isBrailleDisplayKind,
  MULTI_LINE_BRAILLE_PRESETS,
  parseManualBrailleInput,
  selectBrailleDisplayKind,
  selectBraillePreset,
  SINGLE_LINE_BRAILLE_PRESETS,
} from '@util/braillePreset';
import { resolveVersionOptions } from '@util/llm';
import React, { useCallback, useEffect, useId, useState } from 'react';

const MIN_CUSTOM_INSTRUCTION_LENGTH = 10;

// Letter portion of the dialog accelerator keys. Shared between the
// keydown handler and the aria-keyshortcuts attributes so the two
// cannot drift apart and announce a shortcut that no longer fires.
const SAVE_SHORTCUT_KEY = 's';
const CANCEL_SHORTCUT_KEY = 'c';

interface SettingRowProps {
  label: string;
  input: React.ReactNode;
  alignLabel?: 'center' | 'flex-start';
  // When set, the visible label gets this id so the input can use
  // aria-labelledby instead of aria-label, avoiding duplicate
  // announcement of the same text by screen readers.
  labelId?: string;
}

const SettingRow: React.FC<SettingRowProps> = ({ label, input, alignLabel = 'center', labelId }) => (
  <Grid container spacing={1} alignItems={alignLabel} className="settings-grid-container" sx={{ py: 1 }}>
    <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={alignLabel === 'flex-start' ? { py: 1 } : undefined}>
      <Typography id={labelId} variant="body2" fontWeight="normal">
        {label}
      </Typography>
    </Grid>
    <Grid size={{ xs: 12, sm: 6, md: 8 }}>{input}</Grid>
  </Grid>
);

interface BraillePresetSelectProps {
  rowLabel: string;
  placeholder: string;
  presets: readonly BrailleDisplayPreset[];
  selectedPresetId: string | null;
  formatPreset: (preset: BrailleDisplayPreset) => string;
  onPresetChange: (presetId: string) => void;
  hint?: string;
}

const BraillePresetSelect: React.FC<BraillePresetSelectProps> = ({
  rowLabel,
  placeholder,
  presets,
  selectedPresetId,
  formatPreset,
  onPresetChange,
  hint,
}) => {
  const labelId = useId();
  const hintId = useId();
  return (
    <SettingRow
      label={rowLabel}
      labelId={labelId}
      input={(
        <FormControl fullWidth>
          <Select
            value={selectedPresetId ?? ''}
            onChange={e => onPresetChange(e.target.value)}
            fullWidth
            size="small"
            displayEmpty
            slotProps={{
              input: {
                'aria-labelledby': labelId,
                'aria-required': true,
                ...(hint ? { 'aria-describedby': hintId } : {}),
              },
            }}
            MenuProps={{ disablePortal: true }}
          >
            <MenuItem value="" disabled>
              {placeholder}
            </MenuItem>
            {presets.map(preset => (
              <MenuItem key={preset.id} value={preset.id}>
                {formatPreset(preset)}
              </MenuItem>
            ))}
          </Select>
          {hint && (
            <Typography
              id={hintId}
              variant="caption"
              sx={{ mt: 0.5, color: 'text.secondary' }}
            >
              {hint}
            </Typography>
          )}
        </FormControl>
      )}
    />
  );
};

interface LlmModelSettingRowProps {
  modelKey: Llm;
  modelSettings: LlmModelSettings;
  onToggle: (key: Llm, enabled: boolean) => void;
  onChangeKey: (key: Llm, value: string) => void;
  onChangeVersion: (key: Llm, value: LlmVersion) => void;
}

const LlmModelSettingRow: React.FC<LlmModelSettingRowProps> = ({
  modelKey,
  modelSettings,
  onToggle,
  onChangeKey,
  onChangeVersion,
}) => {
  const validVersion = getValidVersion(modelKey, modelSettings.version);
  const [isValidating, setIsValidating] = useState(false);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  // Models available to this credential, probed from the provider's models
  // API (or the local Ollama server) when the credential validates; replaces
  // the curated suggestion list so users pick from what actually exists.
  const [availableModels, setAvailableModels] = useState<string[]>([]);

  // Ollama is a local server: the credential field holds its base URL and
  // "validation" means reachability, so most labels differ from the cloud
  // providers' API-key wording.
  const isOllama = modelKey === 'OLLAMA';
  const credentialLabel = isOllama ? 'Server URL' : 'API Key';

  const getHelperText = (): string => {
    if (!modelSettings.enabled)
      return '';
    if (isValidating)
      return isOllama ? 'Checking Ollama server...' : 'Validating API key...';
    if (isValid === false) {
      return isOllama
        ? 'Ollama server is unreachable. Make sure Ollama is running and, for non-localhost pages, that OLLAMA_ORIGINS allows this site.'
        : `${modelSettings.name} API key is invalid`;
    }
    if (isValid === true)
      return isOllama ? 'Ollama server is reachable' : `${modelSettings.name} API key is valid`;
    return '';
  };

  // The debounce only spaces out request starts; it cannot cancel a request
  // already in flight. isStale lets a superseded cycle (newer keystroke or
  // unmount) discard its response so a slow early probe can never overwrite
  // the state of a newer one.
  const validateApiKey = async (apiKey: string, isStale: () => boolean): Promise<void> => {
    if (!modelSettings.enabled || !apiKey.trim()) {
      if (!isStale()) {
        setIsValid(null);
        setAvailableModels([]);
        // Also clear the spinner: a superseded in-flight request skips its
        // own finally-cleanup as stale, so this cycle owns the state.
        setIsValidating(false);
      }
      return;
    }

    setIsValidating(true);
    try {
      // A single probe answers both credential validity and the live list of
      // models the credential can access, for every provider.
      const probe = await LlmValidationService.probeProvider(modelKey, apiKey);
      if (isStale()) {
        return;
      }
      setIsValid(probe.isValid);
      setAvailableModels(probe.models);
    } catch (error) {
      if (!isStale()) {
        setIsValid(false);
        setAvailableModels([]);
      }
    } finally {
      if (!isStale()) {
        setIsValidating(false);
      }
    }
  };

  useEffect(() => {
    let cancelled = false;
    const debounceTimer = setTimeout(() => {
      validateApiKey(modelSettings.apiKey, () => cancelled);
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(debounceTimer);
    };
  }, [modelSettings.apiKey, modelSettings.enabled, modelKey]);

  const renderMenuItems = (): React.ReactNode[] => {
    const config = MODEL_VERSIONS[modelKey];
    const options: readonly string[] = resolveVersionOptions(
      config.options,
      availableModels,
      validVersion,
    );
    return options.map((version) => {
      const label = config.labels[version as keyof typeof config.labels] ?? version;
      const isSelected = modelSettings.version === version;
      return (
        <MenuItem
          key={version}
          value={version}
          className={`llm-model-setting-row-menu-item ${isSelected ? 'selected' : ''}`}
        >
          {isSelected && (
            <CheckIcon className="llm-model-setting-row-check-icon" />
          )}
          {label}
        </MenuItem>
      );
    });
  };

  return (
    <SettingRow
      label={modelSettings.name}
      input={(
        <Grid container spacing={1} alignItems="center">
          <Grid>
            <Switch
              checked={modelSettings.enabled}
              onChange={e => onToggle(modelKey, e.target.checked)}
              slotProps={{
                input: { 'aria-label': `Enable ${modelSettings.name}` },
              }}
            />
          </Grid>
          <Grid size={7}>
            <FormControl fullWidth>
              <TextField
                disabled={!modelSettings.enabled}
                fullWidth
                size="small"
                value={modelSettings.apiKey}
                onChange={e => onChangeKey(modelKey, e.target.value)}
                placeholder={
                  isOllama
                    ? 'Enter Ollama server URL (e.g. http://localhost:11434)'
                    : `Enter ${modelSettings.name} API Key`
                }
                type={isOllama ? 'text' : 'password'}
                error={isValid === false}
                helperText={getHelperText()}
                slotProps={{
                  input: {
                    'aria-label': `${modelSettings.name} ${credentialLabel}`,
                    'aria-describedby': `${modelKey}-status`,
                    'endAdornment': (
                      <InputAdornment position="end">
                        <div
                          id={`${modelKey}-status`}
                          role="status"
                          aria-live="polite"
                          aria-label={
                            isValidating
                              ? isOllama
                                ? 'Checking Ollama server'
                                : 'Validating API key'
                              : isValid === true
                                ? isOllama
                                  ? 'Ollama server is reachable'
                                  : 'API key is valid'
                                : isValid === false
                                  ? isOllama
                                    ? 'Ollama server is unreachable'
                                    : 'API key is invalid'
                                  : ''
                          }
                        >
                          {isValidating
                            ? (
                                <CircularProgress size={20} />
                              )
                            : isValid === true
                              ? (
                                  <CheckIcon color="success" />
                                )
                              : isValid === false
                                ? (
                                    <ErrorIcon color="error" />
                                  )
                                : null}
                        </div>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </FormControl>
          </Grid>
          <Grid size={8}>
            <FormControl fullWidth>
              <Select
                value={validVersion}
                onChange={(e) => {
                  const newVersion = e.target.value as LlmVersion;
                  onChangeVersion(modelKey, newVersion);
                }}
                disabled={
                  !modelSettings.enabled
                  || !modelSettings.apiKey.trim()
                  || !isValid
                }
                fullWidth
                size="small"
                slotProps={{
                  input: {
                    'aria-label': `${modelSettings.name} Model Version`,
                  },
                }}
                MenuProps={{
                  disablePortal: true,
                  PaperProps: {
                    className: 'settings-menu-paper',
                  },
                }}
              >
                {renderMenuItems()}
              </Select>
              {isValid === true
                && availableModels.length > 0
                && !availableModels.includes(validVersion) && (
                <Typography
                  variant="caption"
                  color="warning.main"
                  role="status"
                  sx={{ mt: 0.5 }}
                >
                  {`"${validVersion}" is not in ${modelSettings.name}'s current model list — it may have been retired. Consider selecting another model.`}
                </Typography>
              )}
            </FormControl>
          </Grid>
        </Grid>
      )}
    />
  );
};

const Settings: React.FC = () => {
  const id = useId();
  const viewModel = useViewModel('settings');
  const chatViewModel = useViewModel('chat');
  const { general, llm } = viewModel.state;

  // SettingsService normalizes braille display fields at construction
  // before any consumer reads them, so the component-side state already
  // arrives in a coherent shape and does not need to re-normalize here.
  const [generalSettings, setGeneralSettings] = useState<GeneralSettings>(general);
  const [llmSettings, setLlmSettings] = useState<LlmSettings>(llm);

  useEffect(() => {
    viewModel.load();
  }, [viewModel]);

  useEffect(() => {
    setGeneralSettings(general);
    setLlmSettings(llm);
  }, [general, llm]);

  const handleGeneralChange = <K extends keyof GeneralSettings>(
    key: K,
    value: GeneralSettings[K],
  ): void => {
    setGeneralSettings(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleBrailleKindChange = useCallback((kind: BrailleDisplayKind): void => {
    setGeneralSettings((prev) => {
      const slice = selectBrailleDisplayKind(kind, prev.brailleDisplayPresetId);
      return { ...prev, ...slice };
    });
  }, []);

  const handleBraillePresetChange = useCallback(
    (kind: 'single' | 'multi', presetId: string): void => {
      const slice = selectBraillePreset(kind, presetId);
      if (!slice) {
        return;
      }
      setGeneralSettings(prev => ({ ...prev, ...slice }));
    },
    [],
  );

  const handleSingleLinePresetChange = useCallback(
    (presetId: string) => handleBraillePresetChange('single', presetId),
    [handleBraillePresetChange],
  );

  const handleMultiLinePresetChange = useCallback(
    (presetId: string) => handleBraillePresetChange('multi', presetId),
    [handleBraillePresetChange],
  );

  const handleLlmChange = <K extends keyof LlmSettings>(
    key: K,
    value: LlmSettings[K],
  ): void => {
    setLlmSettings(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleLlmModelChange = (
    modelKey: Llm,
    propKey: keyof LlmModelSettings,
    value: string | boolean | LlmVersion,
  ): void => {
    setLlmSettings(prev => ({
      ...prev,
      models: {
        ...prev.models,
        [modelKey]: {
          ...prev.models[modelKey],
          [propKey]:
            propKey === 'apiKey' && typeof value === 'string'
              ? value.trim()
              : value,
        },
      },
    }));
  };

  const handleReset = (): void => {
    viewModel.reset();
    const { general, llm } = viewModel.state;
    setGeneralSettings(general);
    setLlmSettings(llm);
  };

  const handleClose = useCallback((): void => {
    viewModel.toggle();
  }, [viewModel]);

  const handleSave = useCallback((): void => {
    // Clamp before persisting so a Save click before the field blurs
    // can't bypass the [1, MAX] bound. The onChange path intentionally
    // skips range clamping during typing so users can edit through
    // intermediate out-of-range states; this is the commit point.
    const safeGeneral: GeneralSettings = {
      ...generalSettings,
      brailleDisplaySize: clampBrailleSize(generalSettings.brailleDisplaySize),
      brailleDisplayLines: clampBrailleLines(generalSettings.brailleDisplayLines),
    };
    viewModel.saveAndClose({ general: safeGeneral, llm: llmSettings });
    // Update the welcome bubble's model info in place instead of resetting the
    // chat slice, so saving a setting (e.g. volume) never discards an ongoing
    // conversation.
    chatViewModel.updateWelcomeMessage();
  }, [viewModel, chatViewModel, generalSettings, llmSettings]);

  const handleSelectClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleSelectChange = useCallback(
    (
      e: SelectChangeEvent<'basic' | 'intermediate' | 'advanced' | 'custom'>,
    ) => {
      e.stopPropagation();
      handleLlmChange('expertiseLevel', e.target.value);
    },
    [handleLlmChange],
  );

  const isCustomInstructionValid
    = llmSettings.expertiseLevel !== 'custom'
      || llmSettings.customInstruction.length >= MIN_CUSTOM_INSTRUCTION_LENGTH;

  // Dialog-scoped: KeybindingService's hotkeys.filter blocks shortcuts while
  // focus is in a non-MAIDR <input>, which would silently break Alt+s / Alt+c
  // inside the manual cells/lines fields.
  const handleDialogKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>): void => {
      const altOnly = e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey;
      if (!altOnly) {
        return;
      }
      const key = e.key.toLowerCase();
      if (key === SAVE_SHORTCUT_KEY) {
        if (!isCustomInstructionValid) {
          return;
        }
        e.preventDefault();
        handleSave();
      } else if (key === CANCEL_SHORTCUT_KEY) {
        e.preventDefault();
        handleClose();
      }
    },
    [isCustomInstructionValid, handleSave, handleClose],
  );

  return (
    <Dialog
      id={id}
      role="dialog"
      aria-label="Settings"
      open={true}
      maxWidth="sm"
      fullWidth
      disablePortal
      disableEnforceFocus
      onClick={e => e.stopPropagation()}
      onKeyDown={handleDialogKeyDown}
      className="settings-dialog"
    >
      <DialogContent className="settings-dialog-content">
        <Grid size="grow">
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            General Settings
          </Typography>
        </Grid>

        {/* General Settings */}
        <Grid container spacing={0.5}>
          <Grid size={12}>
            <SettingRow
              label="Volume"
              input={(
                <FormControl fullWidth>
                  <Slider
                    value={generalSettings.volume}
                    onChange={(_, value) =>
                      handleGeneralChange('volume', Number(value))}
                    min={0}
                    max={100}
                    step={1}
                    valueLabelDisplay="auto"
                    slotProps={{
                      input: {
                        'aria-valuemin': 0,
                        'aria-valuemax': 100,
                        'aria-label': 'Volume',
                        'aria-labelledby': 'volume-label',
                      },
                    }}
                    className="settings-slider-value-label"
                  />
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={12}>
            <SettingRow
              label="Outline Color"
              input={(
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    type="color"
                    size="small"
                    value={generalSettings.highlightColor}
                    onChange={e =>
                      handleGeneralChange('highlightColor', e.target.value)}
                    slotProps={{
                      input: {
                        inputProps: {
                          'aria-label': 'Highlight Color',
                        },
                      },
                    }}
                  />
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={12}>
            <SettingRow
              label="High Contrast Mode"
              input={(
                <FormControl>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={generalSettings.highContrastMode}
                        onChange={e =>
                          handleGeneralChange('highContrastMode', e.target.checked)}
                        size="small"
                      />
                    )}
                    label={generalSettings.highContrastMode ? 'On' : 'Off'}
                    slotProps={{
                      typography: {
                        variant: 'body2',
                      },
                    }}
                    aria-label="High Contrast Mode"
                  />
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={12}>
            <SettingRow
              label="High Contrast Levels"
              input={(
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    type="number"
                    size="small"
                    value={generalSettings.highContrastLevels}
                    onChange={e =>
                      handleGeneralChange(
                        'highContrastLevels',
                        Number(e.target.value),
                      )}
                    slotProps={{
                      input: {
                        inputProps: {
                          'aria-label': 'High Contrast Levels',
                          'min': 2,
                          'max': 20,
                        },
                      },
                    }}
                  />
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={12}>
            <SettingRow
              label="High Contrast Light Color"
              input={(
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    type="color"
                    size="small"
                    value={generalSettings.highContrastLightColor}
                    onChange={e =>
                      handleGeneralChange('highContrastLightColor', e.target.value)}
                    slotProps={{
                      input: {
                        inputProps: {
                          'aria-label': 'High Contrast Light Color',
                        },
                      },
                    }}
                  />
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={12}>
            <SettingRow
              label="High Contrast Dark Color"
              input={(
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    type="color"
                    size="small"
                    value={generalSettings.highContrastDarkColor}
                    onChange={e =>
                      handleGeneralChange('highContrastDarkColor', e.target.value)}
                    slotProps={{
                      input: {
                        inputProps: {
                          'aria-label': 'High Contrast Dark Color',
                        },
                      },
                    }}
                  />
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={12}>
            <SettingRow
              label="Braille Display"
              alignLabel="flex-start"
              labelId={`${id}-braille-kind-label`}
              input={(
                <FormControl fullWidth>
                  <RadioGroup
                    row
                    value={generalSettings.brailleDisplayKind}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (isBrailleDisplayKind(v)) {
                        handleBrailleKindChange(v);
                      }
                    }}
                    aria-labelledby={`${id}-braille-kind-label`}
                  >
                    <FormControlLabel
                      value="single"
                      control={<Radio size="small" />}
                      label="Single line"
                    />
                    <FormControlLabel
                      value="multi"
                      control={<Radio size="small" />}
                      label="Multi-line"
                    />
                    <FormControlLabel
                      value="manual"
                      control={<Radio size="small" />}
                      label="Configure manually"
                    />
                  </RadioGroup>
                </FormControl>
              )}
            />
          </Grid>
          {generalSettings.brailleDisplayKind === 'single' && (
            <Grid size={12}>
              <BraillePresetSelect
                rowLabel="Single-Line Display"
                placeholder="Select a single-line display"
                presets={SINGLE_LINE_BRAILLE_PRESETS}
                selectedPresetId={generalSettings.brailleDisplayPresetId}
                formatPreset={formatSingleLinePreset}
                onPresetChange={handleSingleLinePresetChange}
                hint={'Don\'t see your display? Choose "Configure manually".'}
              />
            </Grid>
          )}
          {generalSettings.brailleDisplayKind === 'multi' && (
            <Grid size={12}>
              <BraillePresetSelect
                rowLabel="Multi-Line Display"
                placeholder="Select a multi-line display"
                presets={MULTI_LINE_BRAILLE_PRESETS}
                selectedPresetId={generalSettings.brailleDisplayPresetId}
                formatPreset={formatMultiLinePreset}
                onPresetChange={handleMultiLinePresetChange}
                hint={'Don\'t see your display? Choose "Configure manually".'}
              />
            </Grid>
          )}
          {generalSettings.brailleDisplayKind === 'manual' && (
            <Grid
              size={12}
              role="group"
              aria-label="Manual braille display configuration"
            >
              <Grid size={12}>
                <SettingRow
                  label="Braille Display Size"
                  input={(
                    <FormControl fullWidth>
                      <TextField
                        fullWidth
                        type="number"
                        size="small"
                        value={generalSettings.brailleDisplaySize}
                        onChange={(e) => {
                          const next = parseManualBrailleInput(e.target.value);
                          if (next !== null) {
                            handleGeneralChange('brailleDisplaySize', next);
                          }
                        }}
                        onBlur={(e) => {
                          const next = parseManualBrailleInput(e.target.value, clampBrailleSize);
                          if (next !== null) {
                            handleGeneralChange('brailleDisplaySize', next);
                          }
                        }}
                        helperText={`Cells per row on a physical braille display (1-${MAX_BRAILLE_SIZE}).`}
                        slotProps={{
                          input: {
                            inputProps: {
                              'aria-label': 'Braille Display Size',
                              'min': 1,
                              'max': MAX_BRAILLE_SIZE,
                              'step': 1,
                            },
                          },
                        }}
                      />
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid size={12}>
                <SettingRow
                  label="Braille Display Lines"
                  input={(
                    <FormControl fullWidth>
                      <TextField
                        fullWidth
                        type="number"
                        size="small"
                        value={generalSettings.brailleDisplayLines}
                        onChange={(e) => {
                          const next = parseManualBrailleInput(e.target.value);
                          if (next !== null) {
                            handleGeneralChange('brailleDisplayLines', next);
                          }
                        }}
                        onBlur={(e) => {
                          const next = parseManualBrailleInput(e.target.value, clampBrailleLines);
                          if (next !== null) {
                            handleGeneralChange('brailleDisplayLines', next);
                          }
                        }}
                        helperText={`Number of rows on a physical braille display (1-${MAX_BRAILLE_LINES}). Set above 1 to enable multi-line output.`}
                        slotProps={{
                          input: {
                            inputProps: {
                              'aria-label': 'Braille Display Lines',
                              'min': 1,
                              'max': MAX_BRAILLE_LINES,
                              'step': 1,
                            },
                          },
                        }}
                      />
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          )}
          <Grid size={12}>
            <SettingRow
              label="Min Frequency (Hz)"
              input={(
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    type="number"
                    size="small"
                    value={generalSettings.minFrequency}
                    onChange={e =>
                      handleGeneralChange(
                        'minFrequency',
                        Number(e.target.value),
                      )}
                    slotProps={{
                      input: {
                        inputProps: {
                          'aria-label': 'Minimum Frequency',
                          'min': 0,
                        },
                      },
                    }}
                  />
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={12}>
            <SettingRow
              label="Max Frequency (Hz)"
              input={(
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    type="number"
                    size="small"
                    value={generalSettings.maxFrequency}
                    onChange={e =>
                      handleGeneralChange(
                        'maxFrequency',
                        Number(e.target.value),
                      )}
                    slotProps={{
                      input: {
                        inputProps: {
                          'aria-label': 'Maximum Frequency',
                          'min': 0,
                        },
                      },
                    }}
                  />
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={12}>
            <SettingRow
              label="Autoplay Duration (ms)"
              input={(
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    type="number"
                    size="small"
                    value={generalSettings.autoplayDuration}
                    onChange={e =>
                      handleGeneralChange(
                        'autoplayDuration',
                        Number(e.target.value),
                      )}
                    slotProps={{
                      input: {
                        inputProps: {
                          'aria-label': 'Autoplay Duration',
                        },
                      },
                    }}
                  />
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={12}>
            <SettingRow
              label="ARIA Mode"
              input={(
                <FormControl>
                  <RadioGroup
                    row
                    value={generalSettings.ariaMode}
                    onChange={e =>
                      handleGeneralChange(
                        'ariaMode',
                        e.target.value as AriaMode,
                      )}
                    aria-label="ARIA Mode"
                  >
                    <FormControlLabel
                      value="assertive"
                      control={<Radio size="small" />}
                      label="Assertive"
                    />
                    <FormControlLabel
                      value="polite"
                      control={<Radio size="small" />}
                      label="Polite"
                    />
                  </RadioGroup>
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={12}>
            <SettingRow
              label="Hover Mode"
              input={(
                <FormControl>
                  <RadioGroup
                    row
                    value={generalSettings.hoverMode}
                    onChange={e =>
                      handleGeneralChange(
                        'hoverMode',
                        e.target.value as HoverMode,
                      )}
                    aria-label="Hover Mode"
                  >
                    <FormControlLabel
                      value="off"
                      control={<Radio size="small" />}
                      label="Off"
                    />
                    <FormControlLabel
                      value="pointermove"
                      control={<Radio size="small" />}
                      label="Hover"
                    />
                    <FormControlLabel
                      value="click"
                      control={<Radio size="small" />}
                      label="Click"
                    />
                  </RadioGroup>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>

        <Grid size={12}>
          <Divider className="settings-divider" />
        </Grid>

        {/* LLM Settings */}
        <Grid container spacing={0.5} className="settings-section">
          <Grid size={12}>
            <Typography
              variant="h6"
              fontWeight="bold"
              gutterBottom
              className="settings-section-title"
            >
              LLM Settings
            </Typography>
          </Grid>

          {(Object.keys(llmSettings.models) as Llm[]).map((modelKey) => {
            const model = llmSettings.models[modelKey];
            return (
              <Grid size={12} key={modelKey} className="settings-model-row">
                <LlmModelSettingRow
                  modelKey={modelKey}
                  modelSettings={model}
                  onToggle={(key, enabled) =>
                    handleLlmModelChange(key, 'enabled', enabled)}
                  onChangeKey={(key, value) =>
                    handleLlmModelChange(key, 'apiKey', value)}
                  onChangeVersion={(key, value) =>
                    handleLlmModelChange(key, 'version', value)}
                />
              </Grid>
            );
          })}

          {/* Expertise Level */}
          <Grid size={12} className="settings-row">
            <FormControl
              fullWidth
              size="small"
              className="settings-model-select"
            >
              <SettingRow
                label="Expertise Level"
                input={(
                  <Select
                    value={llmSettings.expertiseLevel}
                    onChange={handleSelectChange}
                    onClick={handleSelectClick}
                    slotProps={{
                      input: {
                        'aria-label': 'Expertise Level',
                      },
                    }}
                    MenuProps={{
                      disablePortal: true,
                      PaperProps: {
                        className: 'llm-model-setting-select-menu',
                      },
                    }}
                  >
                    <MenuItem value="basic">Basic</MenuItem>
                    <MenuItem value="intermediate">Intermediate</MenuItem>
                    <MenuItem value="advanced">Advanced</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                )}
              />
            </FormControl>
          </Grid>

          {/* Custom Instructions - Only show when custom is selected */}
          {llmSettings.expertiseLevel === 'custom' && (
            <Grid size={12}>
              <Grid
                container
                spacing={1}
                alignItems="flex-start"
                sx={{ py: 1 }}
              >
                <Grid size={12} sx={{ py: 1 }}>
                  <Typography variant="body2" fontWeight="normal">
                    Custom Instructions
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <FormControl fullWidth>
                    <TextareaAutosize
                      minRows={3}
                      maxRows={6}
                      value={llmSettings.customInstruction}
                      onChange={e =>
                        handleLlmChange('customInstruction', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                      }}
                      placeholder="Enter custom instruction..."
                      aria-label="Custom Instructions"
                    />
                  </FormControl>
                </Grid>
                {llmSettings.customInstruction.length
                  < MIN_CUSTOM_INSTRUCTION_LENGTH && (
                  <Grid size={12} sx={{ mt: 1 }}>
                    <Alert severity="warning">
                      Custom instructions must be at least
                      {' '}
                      {MIN_CUSTOM_INSTRUCTION_LENGTH}
                      {' '}
                      characters long
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}
        </Grid>

        <Grid size={12}>
          <Divider className="settings-divider" />
        </Grid>
      </DialogContent>

      {/* Footer Actions */}
      <Grid
        container
        component={DialogActions}
        alignItems="center"
        className="settings-footer"
      >
        <Grid size="auto" className="settings-grid-padding">
          <Button
            variant="text"
            color="inherit"
            onClick={handleReset}
            aria-label="Reset Settings"
          >
            Reset
          </Button>
        </Grid>
        <Grid
          size="grow"
          container
          spacing={1}
          justifyContent="flex-end"
          className="settings-footer-actions"
        >
          <Grid size="auto">
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleClose}
              aria-label="Close Settings with no changes"
              aria-keyshortcuts={`Alt+${CANCEL_SHORTCUT_KEY}`}
            >
              Close
            </Button>
          </Grid>
          <Grid size="auto">
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={!isCustomInstructionValid}
              title={
                !isCustomInstructionValid
                  ? `Custom instructions must be at least ${MIN_CUSTOM_INSTRUCTION_LENGTH} characters long`
                  : ''
              }
              aria-label="Save & Close Settings"
              aria-keyshortcuts={`Alt+${SAVE_SHORTCUT_KEY}`}
            >
              Save & Close
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default Settings;
